import { useEffect, useRef } from "react";

/**
 * Sendbird UI Kit의 DOM 변경을 감지하고 커스터마이징하는 커스텀 훅
 * MutationObserver를 사용하여 DOM 변경을 감시합니다.
 */
export const useSendbirdDomObserver = (
  currentChannel: any,
  showChannel: boolean,
  authUser: any,
) => {
  const observerRef = useRef<MutationObserver | null>(null);

  // 봇 메시지 스타일링
  const styleBotMessages = () => {
    const messageElements = document.querySelectorAll(".sendbird-user-message");

    messageElements.forEach((element) => {
      const messageWrapper = element.closest(".sendbird-message-content");
      if (!messageWrapper) return;

      const senderName = messageWrapper.querySelector(
        ".sendbird-message-content__middle__sender-name",
      );

      if (senderName?.textContent?.includes("봇")) {
        element.classList.add("bot-message");

        // 봇 메시지 스타일 적용
        const messageBody = element.querySelector(
          ".sendbird-user-message__body",
        );
        if (messageBody) {
          (messageBody as HTMLElement).style.backgroundColor = "#f0f0f0";
          (messageBody as HTMLElement).style.color = "#333";
        }
      }
    });
  };

  // 채널 아바타 업데이트
  const updateChannelAvatars = () => {
    const channelPreviews = document.querySelectorAll(
      ".sendbird-channel-preview",
    );

    channelPreviews.forEach((preview) => {
      const avatarElement = preview.querySelector(
        ".sendbird-channel-preview__avatar",
      );
      const channelNameElement = preview.querySelector(
        ".sendbird-channel-preview__content__name",
      );

      if (avatarElement && channelNameElement) {
        const channelName = channelNameElement.textContent;

        // 방송 채널 체크
        if (
          channelName?.includes("전체 알림") ||
          channelName?.includes("공지")
        ) {
          avatarElement.classList.add("broadcast-channel");
        }
      }
    });
  };

  // 메시지 타임스탬프 포맷팅
  const formatMessageTimestamps = () => {
    const timestamps = document.querySelectorAll(
      ".sendbird-message-content__middle__body-container__created-at",
    );

    timestamps.forEach((timestamp) => {
      const timeText = timestamp.textContent;
      if (timeText && !timestamp.classList.contains("formatted")) {
        // 포맷팅 로직 (필요에 따라 수정)
        timestamp.classList.add("formatted");
      }
    });
  };

  // Read receipts 업데이트
  const updateReadReceipts = () => {
    const doneAllIcons = document.querySelectorAll(
      'svg[className*="sendbird-icon"][className*="icon-done-all"]',
    );

    doneAllIcons.forEach((icon) => {
      const fillColor = icon.getAttribute("fill");
      const parent = icon.parentElement;

      if (parent && !parent.querySelector(".custom-read-status")) {
        const readStatus = document.createElement("span");
        readStatus.className = "custom-read-status";
        readStatus.textContent = fillColor === "#166DD7" ? "읽음" : "안읽음";
        readStatus.style.fontSize = "11px";
        readStatus.style.marginLeft = "4px";
        readStatus.style.color = fillColor === "#166DD7" ? "#166DD7" : "#999";
        parent.appendChild(readStatus);
      }
    });
  };

  // 모든 커스터마이징 적용
  const applyAllCustomizations = () => {
    styleBotMessages();
    updateChannelAvatars();
    formatMessageTimestamps();
    updateReadReceipts();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!showChannel) return;

    // MutationObserver 설정
    if (!observerRef.current) {
      observerRef.current = new MutationObserver((mutations) => {
        // DOM 변경 감지 시 커스터마이징 적용
        applyAllCustomizations();
      });
    }

    // 감시 대상 설정
    const targetNode = document.querySelector(".sendbird-conversation");
    const channelListNode = document.querySelector(".sendbird-channel-list");

    if (targetNode) {
      observerRef.current.observe(targetNode, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "fill"],
      });
    }

    if (channelListNode) {
      observerRef.current.observe(channelListNode, {
        childList: true,
        subtree: true,
      });
    }

    // 초기 커스터마이징 적용
    applyAllCustomizations();

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [showChannel, currentChannel, authUser]);

  return {
    applyAllCustomizations,
    styleBotMessages,
    updateChannelAvatars,
    formatMessageTimestamps,
    updateReadReceipts,
  };
};
